export function createFukushizaiContextMutationUiService(deps) {
    // item/group を正規化して section refresh 用の context を組み立てます。
    function buildRefreshContext(itemCd, groupNo) {
        const normalizedItem = deps.normalizeItemCd(itemCd);
        const normalizedGroup = deps.normalizeGroupNo(groupNo);
        return {
            itemCd: normalizedItem,
            groupNo: normalizedGroup,
            colorCd: deps.selectedFukushizaiContextRef.current?.colorCd
        };
    }

    // group mutation 後の UI と entry 投影を共通で再同期します。
    function applyFukushizaiMutationRefresh(options = {}) {
        const context = buildRefreshContext(
            deps.DicVal(options, "itemCd", ""),
            deps.DicVal(options, "groupNo", "")
        );
        if (context.itemCd === "" || context.groupNo === "") return false;

        const clearMergedFieldsFirst = deps.DicVal(options, "clearMergedFieldsFirst", false) === true;
        const clearPendingPreview = deps.DicVal(options, "clearPendingPreview", false) === true;
        const unlockPreviewGrid = deps.DicVal(options, "unlockPreviewGrid", false) === true;
        const markModified = deps.DicVal(options, "markModified", false) === true;
        const previewOverride = deps.DicVal(options, "previewOverride", null);

        deps.updateSelectedFukushizaiContext(context);
        deps.setSelectedSetItem(context.itemCd);
        deps.setSelectedGroup(context.groupNo);

        // 投影を作り直す前に entrydata 上の副資材投影を明示的に掃除します。
        if (clearMergedFieldsFirst) {
            deps.clearFukushizaiMergedFields(deps.entrydataRef.current);
        }

        deps.mergeFukushizaiRepresentativeRowToEntry(
            deps.entrydataRef.current,
            deps.entrydetailRef.current,
            { itemCd: context.itemCd, groupNo: context.groupNo }
        );
        deps.primeFukushizaiMasterStoreFromRows(
            deps.getDetailRowsByPanelType(deps.entrydetailRef.current, deps.FUKUSHIZAI_BASE_PANEL_TYPE)
        );

        // preview 確定時は preview 状態を先に解除して通常表示へ戻します。
        if (clearPendingPreview) {
            deps.setPendingFukushizaiCopy(null);
        }
        if (unlockPreviewGrid) {
            deps.refreshFukushizaiCopyPreviewGridEditLock(false);
        }

        deps.refreshSectionViews(context.itemCd, context.groupNo, previewOverride, context);
        deps.scheduleFukushizaiSectionGridRepaint();

        // 明示的な state 変更があったときだけ modify を立てます。
        if (markModified && !deps.isEmpty(deps.fnc.current) && typeof deps.fnc.current.modify === "function") {
            deps.fnc.current.modify(true);
        }

        // 単票(BASE)の入力コンポーネントを entrydata から再同期させる。
        // これを省くとグループ追加/削除/編集後に旧グループの表示値(下札等)が単票に残る。
        if (typeof deps.dispatchEntryRerender === "function") {
            deps.dispatchEntryRerender("fukushizaiGroupMutation");
        }
        deps.rerender();
        return true;
    }

    // group add/edit/delete 後の既存後処理をそのまま service 化します。
    function refreshAfterFukushizaiGroupMutation(itemCd, groupNo, changed) {
        if (!changed) return;
        applyFukushizaiMutationRefresh({
            itemCd,
            groupNo,
            clearMergedFieldsFirst: true,
            markModified: true,
            previewOverride: null
        });
    }

    // preview cancel 後の UI 復元だけを共通化します。
    function cancelFukushizaiCopyPreviewUi() {
        const preview = typeof deps.getPendingFukushizaiCopy === "function"
            ? deps.getPendingFukushizaiCopy()
            : null;

        deps.setPendingFukushizaiCopy(null);
        deps.refreshFukushizaiCopyPreviewGridEditLock(false);

        // preview があった場合だけ対象 context の通常表示を組み直します。
        if (!deps.isEmpty(preview)) {
            deps.refreshSectionViews(
                deps.DicVal(preview, "targetItem", ""),
                deps.DicVal(preview, "targetGroup", ""),
                null,
                {
                    itemCd: deps.DicVal(preview, "targetItem", ""),
                    groupNo: deps.DicVal(preview, "targetGroup", ""),
                    colorCd: deps.selectedFukushizaiContextRef.current?.colorCd
                }
            );
        }

        deps.scheduleFukushizaiSectionGridRepaint();
        deps.rerender();
    }

    return {
        refreshAfterFukushizaiGroupMutation,
        applyFukushizaiMutationRefresh,
        cancelFukushizaiCopyPreviewUi
    };
}
